import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { Icon, type IconName } from './icons/Icon';
import s from './Button.module.css';
import { cx } from './cx';

export type ButtonVariant = 'primary' | 'ghost' | 'gold' | 'light' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface Common {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  /** Icon after the label (e.g. "fwd" on a primary call to action). */
  iconEnd?: IconName;
  block?: boolean;
  /** Shows a spinner, keeps the width and blocks clicks. */
  busy?: boolean;
  children?: ReactNode;
}

export type ButtonProps = Common & ButtonHTMLAttributes<HTMLButtonElement> & { ref?: Ref<HTMLButtonElement> };
export type LinkButtonProps = Common &
  AnchorHTMLAttributes<HTMLAnchorElement> & { href: string; ref?: Ref<HTMLAnchorElement> };

function classes(p: Common, extra?: string) {
  return cx(
    s.btn,
    p.variant && p.variant !== 'primary' && s[p.variant],
    p.size === 'sm' && s.sm,
    p.size === 'lg' && s.lg,
    p.block && s.block,
    p.busy && s.busy,
    extra,
  );
}

function Inner({ icon, iconEnd, children }: Common) {
  return (
    <>
      {icon ? <Icon name={icon} size="sm" /> : null}
      {children}
      {iconEnd ? <Icon name={iconEnd} size="sm" /> : null}
    </>
  );
}

export function Button(props: ButtonProps) {
  const {
    variant,
    size,
    icon,
    iconEnd,
    block,
    busy,
    children,
    className,
    type = 'button',
    disabled,
    ...rest
  } = props;
  return (
    <button
      type={type}
      className={classes({ variant, size, block, busy }, className)}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      <Inner icon={icon} iconEnd={iconEnd}>
        {children}
      </Inner>
    </button>
  );
}

/** A link styled as a button (navigation, downloads). */
export function LinkButton(props: LinkButtonProps) {
  const { variant, size, icon, iconEnd, block, busy, children, className, ...rest } = props;
  return (
    <a className={classes({ variant, size, block, busy }, className)} {...rest}>
      <Inner icon={icon} iconEnd={iconEnd}>
        {children}
      </Inner>
    </a>
  );
}

/** The round close / icon-only button. A label is required for screen readers. */
export function IconButton(
  props: ButtonHTMLAttributes<HTMLButtonElement> & {
    icon: IconName;
    label: string;
    ref?: Ref<HTMLButtonElement>;
  },
) {
  const { icon, label, className, type = 'button', ...rest } = props;
  return (
    <button type={type} className={cx(s.xbtn, className)} aria-label={label} title={label} {...rest}>
      <Icon name={icon} size="sm" />
    </button>
  );
}
